import React from 'react';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';

import { BLACK } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';

import { getReliabilityRingColor, RELIABILITY_RING_STOPS } from './jobOrderCardModel';

const RING_SIZE = 44;
const RING_STROKE = 6;
// Arc starts at 3 o'clock and grows clockwise, matching the score gauge.
const RING_START_DEG = 90;

const clampScore = (value) => Math.min(100, Math.max(0, value));

const ringGradient = (progress) => {
  if (progress >= 100) {
    return getReliabilityRingColor(100);
  }
  const stops = RELIABILITY_RING_STOPS.filter((stop) => stop.at < progress).map(
    (stop) => `${stop.color} ${stop.at}%`
  );
  stops.push(`${getReliabilityRingColor(progress)} ${progress}%`);
  stops.push(`transparent ${progress}%`);
  return `conic-gradient(from ${RING_START_DEG}deg, ${stops.join(', ')})`;
};

const capStyle = (percent, color) => {
  const radius = (RING_SIZE - RING_STROKE) / 2;
  const center = RING_SIZE / 2;
  const theta = ((RING_START_DEG + (percent / 100) * 360) * Math.PI) / 180;
  return {
    position: 'absolute',
    width: RING_STROKE,
    height: RING_STROKE,
    borderRadius: '50%',
    backgroundColor: color,
    left: center + radius * Math.sin(theta) - RING_STROKE / 2,
    top: center - radius * Math.cos(theta) - RING_STROKE / 2,
  };
};

const labelSx = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: PRIMARY_FONT[700],
  lineHeight: 1,
};

const ReliabilityScoreRing = ({ value }) => {
  const hasScore = typeof value === 'number' && !Number.isNaN(value);
  const progress = hasScore ? clampScore(value) : 0;
  const label = hasScore ? value : '--';

  if (!hasScore) {
    return (
      <Box
        sx={{
          width: RING_SIZE,
          height: RING_SIZE,
          boxSizing: 'border-box',
          borderRadius: '50%',
          border: `3px solid ${BLACK[40]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: BLACK[40],
          fontFamily: PRIMARY_FONT[700],
          fontSize: '14px',
          lineHeight: 1,
        }}
      >
        {label}
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: RING_SIZE, height: RING_SIZE, flexShrink: 0 }}>
      <Box
        data-testid="job-order-card-reliability-arc"
        data-progress={progress}
        sx={{
          width: RING_SIZE,
          height: RING_SIZE,
          borderRadius: '50%',
          background: ringGradient(progress),
          mask: `radial-gradient(farthest-side, transparent calc(100% - ${RING_STROKE}px), #000 calc(100% - ${RING_STROKE}px))`,
          WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${RING_STROKE}px), #000 calc(100% - ${RING_STROKE}px))`,
        }}
      />
      {progress < 100 && (
        <Box
          sx={capStyle(0, getReliabilityRingColor(0))}
          data-testid="reliability-ring-cap-start"
        />
      )}
      {progress > 0 && progress < 100 && (
        <Box
          sx={capStyle(progress, getReliabilityRingColor(progress))}
          data-testid="reliability-ring-cap-end"
        />
      )}
      <Box
        sx={{
          ...labelSx,
          color: BLACK[90],
          fontSize: String(label).length > 2 ? '12px' : '14px',
        }}
      >
        {label}
      </Box>
    </Box>
  );
};

ReliabilityScoreRing.propTypes = {
  value: PropTypes.number,
};

export default ReliabilityScoreRing;
